/*
 * CAPTURE-OWNED MODULE
 * DocTypeMatchList — the similar existing document types shown under a new
 * type's name while it is typed (utils/docTypeSuggestions). Used by the
 * Template Studio selector's "+ New type" row and the Document Types page.
 *
 * It only shows; the caller decides what blocks. Active types get "Use
 * existing" when the caller passes onUseExisting. Inactive types get no action
 * at all: turning one on is a Document Types page decision (reactivate-and-use
 * is an open product question, not built).
 */
import React, { useState } from 'react';
import { DEFAULT_VISIBLE_MATCHES } from '../../utils/fieldMatch';
import './DocTypeMatchList.css';

export default function DocTypeMatchList({
  suggestions,
  onUseExisting,
  inactiveHint = 'Inactive types can be turned on in Document Types.',
}) {
  const [showAll, setShowAll] = useState(false);
  const { collision, keyEmpty, matches = [] } = suggestions || {};

  if (keyEmpty) {
    return (
      <div className="dtm" data-testid="doc-type-matches">
        <p className="dtm-collision" role="alert">
          The name needs at least one letter or digit.
        </p>
      </div>
    );
  }
  if (matches.length === 0) return null;

  const visible = showAll ? matches : matches.slice(0, DEFAULT_VISIBLE_MATCHES);
  const anyInactive = matches.some((m) => !m.active);

  return (
    <div className="dtm" data-testid="doc-type-matches">
      {collision && (
        <p className="dtm-collision" role="alert">
          This name matches the existing type <strong>{collision.label || collision.key}</strong>.
        </p>
      )}
      <div className="dtm-title">Similar existing types</div>
      <ul className="dtm-list">
        {visible.map((m) => (
          <li key={`${m.item.tenant_id || 'global'}:${m.item.key}`} className="dtm-row" data-testid={`doc-type-match-${m.item.key}`}>
            <span className="dtm-label">{m.item.label || m.item.key}</span>
            <code className="dtm-key">{m.item.key}</code>
            <span className={`dtm-tag ${m.active ? 'dtm-tag-active' : 'dtm-tag-inactive'}`}>
              {m.active ? 'Active' : 'Inactive'}
            </span>
            {m.active && onUseExisting && (
              <button
                type="button"
                className="btn btn-sm btn-secondary dtm-use"
                onClick={() => onUseExisting(m.item)}
              >
                Use existing
              </button>
            )}
          </li>
        ))}
      </ul>
      {!showAll && matches.length > DEFAULT_VISIBLE_MATCHES && (
        <button type="button" className="dtm-show-all" onClick={() => setShowAll(true)}>
          Show all {matches.length}
        </button>
      )}
      {anyInactive && <div className="dtm-hint">{inactiveHint}</div>}
    </div>
  );
}
